import { AxiosResponse } from 'axios';
import { useCallback } from 'react';
import { ApiUrlMobile } from '@/mobile/utils/constants';
import { getSigV4HeadersMobileForGet } from '@/utils/cognito';
import { RentalWifiSendBackInfoGetResponse } from '@/mobile/types/api/mypage/mobileRentalWifiSendBackInfo';
import useCommonApiErrorHandling from '@/hooks/useCommonApiErrorHandling';
import useInsertServiceIdInApiUrl from '@/hooks/useInsertServiceIdInApiUrl';
import apiMobile from '@/mobile/utils/apiMobile';
import useSetLoading from '@/hooks/useSetLoading';
import initialCollectionAddressState from '@/mobile/states/rentalwifi/collectionSendBackAddressState';
import { useSetRecoilState } from 'recoil';

const apiUrl = ApiUrlMobile.Read.RENTAL_WIFI_SEND_BACK_INFO;

/**
 * 回収先住所情報操作用カスタムフック
 * API呼び出し・ローディング制御・エラーハンドリングを共通化し、
 * 呼び出し元では execute() を呼ぶだけで操作処理が完結する。
 *
 * @returns execute - API操作処理を実行する関数
 */
const useGetRentalWifiSendBackInfo = () => {
  const { startLoading, finishLoading } = useSetLoading();
  const { insertServiceIdInApiUrl } = useInsertServiceIdInApiUrl();
  const { commonErrorHandling, commonResponseCodeHandling } = useCommonApiErrorHandling();
  const setRentalWifiSendBackInfo = useSetRecoilState(initialCollectionAddressState);

  const execute = useCallback(async () => {
    const apiUrlWithServiceId = insertServiceIdInApiUrl(apiUrl);

    const headers = await getSigV4HeadersMobileForGet(apiUrlWithServiceId);
    startLoading(apiUrlWithServiceId);

    await apiMobile
      .get(apiUrlWithServiceId, headers)
      .then(({ data }: AxiosResponse<RentalWifiSendBackInfoGetResponse>) => {
        const { isError } = commonResponseCodeHandling({
          responseCode: data.response_code,
          isTransitionTop: true,
        });

        if (isError) return;

        // 画面側で必要なのは返却先住所のみなので、ネストした情報だけを取り出して state を単純化しておく。
        setRentalWifiSendBackInfo(data.send_back_info);
      })
      .catch((error: any) => {
        commonErrorHandling({ error, isTransitionTop: true });
      })
      .finally(() => finishLoading(apiUrlWithServiceId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { execute };
};

export default useGetRentalWifiSendBackInfo;
